import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { loadBest, saveBest, type GameId } from './game';

export type Round = { key: string; options: string[]; answer: string };
export type QuizState = { score: number; hits: number; misses: number; index: number; total: number };

const NEXT_DELAY = 1300;

// Arrow keys move focus between a group's buttons like a joystick: up/down change row, left/right
// change column. `data-cols` on the group says how many columns its grid has (default 1).
export function moveFocus(e: KeyboardEvent<HTMLElement>) {
  const cols = Number(e.currentTarget.dataset.cols) || 1;
  const vertical = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
  const horizontal = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
  if (!vertical && !horizontal) return;
  e.preventDefault();
  const btns = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('button')];
  const i = btns.indexOf(document.activeElement as HTMLButtonElement);
  if (i < 0) { btns[0]?.focus(); return; }
  let next = i;
  if (vertical) next = i + vertical * cols;
  // In a single column, left/right act like up/down; in a grid they stay on the row.
  else if (cols === 1) next = i + horizontal;
  else if ((i % cols) + horizontal >= 0 && (i % cols) + horizontal < cols) next = i + horizontal;
  btns[Math.max(0, Math.min(btns.length - 1, next))]?.focus();
}

// Runs multiple-choice rounds: prompt, options, feedback, auto-advance, end card.
export default function Quiz<R extends Round>({ game, makeRounds, renderStage, scoreFor, missPenalty = 0, maxScore, verdict, onExit, hud, over }: {
  game: GameId;
  makeRounds: () => R[];
  renderStage: (round: R, answered: boolean) => ReactNode;
  /** Points for a correct answer, given how long the visitor took. */
  scoreFor: (ms: number) => number;
  /** Points lost for a wrong answer. The final score never shows below zero. */
  missPenalty?: number;
  maxScore: number;
  verdict: (s: QuizState) => string;
  onExit: () => void;
  /** Replaces the default round/score line. */
  hud?: (s: QuizState) => ReactNode;
  /** Ends the game before the rounds run out. */
  over?: (s: QuizState) => boolean;
}) {
  const [rounds, setRounds] = useState(makeRounds);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  // Kept unclamped so early mistakes still count against later points; shown floored at zero.
  const [rawScore, setScore] = useState(0);
  const score = Math.max(0, rawScore);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [best, setBest] = useState(() => loadBest(game));
  const [feedback, setFeedback] = useState('');
  const startedAt = useRef(performance.now());
  const optionsRef = useRef<HTMLDivElement>(null);
  const againRef = useRef<HTMLButtonElement>(null);
  const state: QuizState = { score, hits, misses, index, total: rounds.length };
  const done = !picked && (index >= rounds.length || !!over?.(state));
  const round = rounds[index];

  useEffect(() => {
    startedAt.current = performance.now();
    optionsRef.current?.querySelector('button')?.focus({ preventScroll: true });
  }, [index, rounds]);

  useEffect(() => {
    if (!picked) return;
    const t = setTimeout(() => { setPicked(null); setFeedback(''); setIndex(i => i + 1); }, NEXT_DELAY);
    return () => clearTimeout(t);
  }, [picked]);

  useEffect(() => {
    if (!done) return;
    setBest(saveBest(game, score));
    againRef.current?.focus({ preventScroll: true });
  }, [done, game, score]);

  const pick = (option: string) => {
    if (picked || done) return;
    const correct = option === round.answer;
    const points = correct ? scoreFor(performance.now() - startedAt.current) : 0;
    setPicked(option);
    setScore(s => s + (correct ? points : -missPenalty));
    if (correct) setHits(h => h + 1); else setMisses(m => m + 1);
    setFeedback(correct ? `Correct! +${points}` : `Nope, it was ${round.answer}.${missPenalty ? ` −${missPenalty}` : ''}`);
  };

  const again = () => {
    setRounds(makeRounds());
    setIndex(0);
    setScore(0);
    setHits(0);
    setMisses(0);
    setFeedback('');
  };

  if (done) return (
    <div className="game-end">
      <p className="game-end-score"><strong>{score}</strong> / {maxScore}</p>
      <p className="game-end-verdict">{verdict(state)}</p>
      <p className="game-end-best">Hi-score {best}</p>
      <div className="game-end-actions" onKeyDown={moveFocus}>
        <button ref={againRef} type="button" className="arc-btn arc-btn-solid" onClick={again}>Play again</button>
        <button type="button" className="arc-btn" onClick={onExit}>Back to games</button>
      </div>
    </div>
  );

  return (
    <div className="game">
      {hud ? hud(state) : (
        <div className="game-meta">
          <span>Round {index + 1}/{rounds.length}</span>
          <span>Score {score}</span>
        </div>
      )}
      <div className="game-stage">{renderStage(round, !!picked)}</div>
      <div ref={optionsRef} className="game-options" data-cols={2} onKeyDown={moveFocus}>
        {round.options.map(o => {
          const state = picked && (o === round.answer ? ' is-right' : o === picked ? ' is-wrong' : '');
          return (
            <button key={`${round.key}-${o}`} type="button" className={`game-option${state || ''}`}
              aria-disabled={!!picked} onClick={() => pick(o)}>
              {o}
            </button>
          );
        })}
      </div>
      <p className="game-feedback" role="status" aria-live="polite">{feedback}</p>
    </div>
  );
}
